/**
 * useVoiceInput — thin wrapper around the browser Web Speech API.
 *
 * Free · instant · works in Chrome/Edge/Safari (via `webkitSpeechRecognition`).
 * We deliberately do NOT round-trip audio to a paid server (Whisper etc.) —
 * this keeps voice search free at every scale.
 *
 * If the browser lacks the API, `supported=false` and consumers can hide
 * their mic UI.
 *
 * Locale hookup: pass a language code (e.g. "hi", "en", "es"); we map to
 * the BCP-47 tag the API expects.
 */
import { useEffect, useRef, useState, useCallback } from "react";

type VoiceState = "idle" | "listening" | "processing" | "error";

interface Options {
  /** ISO 639-1 code from the Sancta locale hook. Defaults to "en". */
  language?: string;
  /** Auto-stop after N ms of silence. Defaults to 2500ms. */
  silenceTimeoutMs?: number;
  /** Called with the final transcript once recognition ends. */
  onResult?: (transcript: string) => void;
  /**
   * Continuous conversation mode — on each silence gap the utterance is
   * sent via onResult but the mic KEEPS listening until the user taps stop.
   * (Phase 3: "mic activates on click and stays on until the user stops it".)
   */
  continuousSend?: boolean;
}

// Map ISO 639-1 → BCP-47 (locale) codes the browser expects
const LOCALE_MAP: Record<string, string> = {
  en: "en-US",
  hi: "hi-IN",
  ta: "ta-IN",
  te: "te-IN",
  kn: "kn-IN",
  ml: "ml-IN",
  bn: "bn-IN",
  mr: "mr-IN",
  gu: "gu-IN",
  es: "es-ES",
  ar: "ar-SA",
  fr: "fr-FR",
};

function _getRecognitionCtor(): any {
  if (typeof window === "undefined") return null;
  return (
    (window as any).SpeechRecognition ||
    (window as any).webkitSpeechRecognition ||
    null
  );
}

export function useVoiceInput({
  language = "en",
  silenceTimeoutMs = 2500,
  onResult,
  continuousSend = false,
}: Options = {}) {
  const [state, setState] = useState<VoiceState>("idle");
  const [transcript, setTranscript] = useState("");
  const [error, setError] = useState<string | null>(null);

  const ctor = _getRecognitionCtor();
  const supported = ctor !== null;

  const recognitionRef = useRef<any>(null);
  const silenceTimerRef = useRef<any>(null);
  const finalTranscriptRef = useRef("");
  const onResultRef = useRef(onResult);
  useEffect(() => { onResultRef.current = onResult; }, [onResult]);
  const userStoppedRef = useRef(false);

  function _clearSilenceTimer() {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }

  const stop = useCallback(() => {
    userStoppedRef.current = true;
    _clearSilenceTimer();
    try { recognitionRef.current?.stop(); } catch { /* ignore */ }
  }, []);

  const start = useCallback(() => {
    if (!supported) {
      setError("Voice input is not supported in this browser.");
      setState("error");
      return;
    }
    if (state === "listening") return;
    userStoppedRef.current = false;
    finalTranscriptRef.current = "";
    setTranscript("");
    setError(null);

    const rec = new ctor();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = LOCALE_MAP[language] || "en-US";

    rec.onstart = () => setState("listening");

    rec.onresult = (ev: any) => {
      let interim = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const t = ev.results[i][0].transcript;
        if (ev.results[i].isFinal) finalTranscriptRef.current += t + " ";
        else interim += t;
      }
      setTranscript((finalTranscriptRef.current + interim).trim());

      // Reset silence timer — arm again for the next quiet window
      _clearSilenceTimer();
      silenceTimerRef.current = setTimeout(() => {
        if (continuousSend) {
          // Send this utterance but keep the mic open for the next one.
          const t = finalTranscriptRef.current.trim();
          if (t) {
            try { onResultRef.current?.(t); } catch { /* consumer error */ }
            finalTranscriptRef.current = "";
            setTranscript("");
          }
        } else {
          try { rec.stop(); } catch { /* ignore */ }
        }
      }, silenceTimeoutMs);
    };

    rec.onerror = (ev: any) => {
      _clearSilenceTimer();
      const code = ev.error || "unknown";
      // Fatal errors must NOT auto-restart in continuous mode — otherwise the
      // mic loops and spams errors (very common on mobile Safari / in-app
      // browsers where the speech service is unavailable).
      const fatal = ["not-allowed", "service-not-allowed", "audio-capture"].includes(code);
      if (fatal) userStoppedRef.current = true;
      const msg =
        code === "no-speech"
          ? "I didn't catch that — try again."
          : code === "not-allowed"
            ? "Microphone access is blocked. Enable mic permission in your browser settings."
            : code === "service-not-allowed"
              ? "Voice typing isn't available in this browser — please type your message, or try Chrome on desktop."
              : code === "audio-capture"
                ? "No microphone found on this device."
                : `Voice input failed (${code}).`;
      setError(msg);
      setState("error");
    };

    rec.onend = () => {
      _clearSilenceTimer();
      const finalT = finalTranscriptRef.current.trim();
      if (finalT) {
        setTranscript(finalT);
        setState("processing");
        try { onResultRef.current?.(finalT); } catch { /* consumer error */ }
        finalTranscriptRef.current = "";
      }
      // Continuous mode: the engine sometimes ends on its own — restart to
      // keep the mic open until the user explicitly stops.
      if (continuousSend && !userStoppedRef.current) {
        try { rec.start(); return; } catch { /* fall through to idle */ }
      }
      setState("idle");
    };

    recognitionRef.current = rec;
    try { rec.start(); }
    catch (e: any) {
      setError(e?.message || "Could not start voice input.");
      setState("error");
    }
  }, [supported, ctor, language, silenceTimeoutMs, state, continuousSend]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      _clearSilenceTimer();
      try { recognitionRef.current?.abort(); } catch { /* ignore */ }
    };
  }, []);

  return { state, transcript, error, supported, start, stop };
}
